import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";

const empty = {
  contact_name: "", contact_phone: "", contact_email: "",
  property_type: "residential", address: "", suburb: "",
  num_cameras: 4, installation_type: "wired", notes: "",
  price: "", breakdown: "",
};

export default function NewQuoteDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(empty);
  const [priceNow, setPriceNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const reset = () => { setForm(empty); setPriceNow(false); };

  const submit = async () => {
    if (!form.contact_name.trim()) { toast.error("Customer name is required"); return; }
    if (priceNow && form.contact_email && !form.price) { toast.error("Add a price, or turn off 'Price now' to send later"); return; }
    setBusy(true);
    try {
      const payload = { ...form, price: priceNow && form.price ? parseFloat(form.price) : null };
      const { data } = await api.post("/admin/quotes", payload);
      toast.success(priceNow && form.contact_email ? "Quote created and emailed to customer" : "Quote created");
      reset();
      onCreated?.(data);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Phone className="h-5 w-5 text-eagle-gold" />New Quote</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2 mb-2">For a customer who called or walked in — no account needed on their end.</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Customer name *</Label><Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="new-quote-name" /></div>
            <div><Label>Phone</Label><Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="new-quote-phone" /></div>
          </div>
          <div><Label>Email <span className="text-xs text-muted-foreground">(needed to send them anything by email)</span></Label><Input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="new-quote-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="new-quote-address" /></div>
            <div><Label>Suburb</Label><Input value={form.suburb} onChange={(e) => set("suburb", e.target.value)} data-testid="new-quote-suburb" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Property</Label>
              <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger data-testid="new-quote-property"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="residential">Residential</SelectItem><SelectItem value="commercial">Commercial</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label># Cameras</Label><Input type="number" min="0" value={form.num_cameras} onChange={(e) => set("num_cameras", parseInt(e.target.value || 0))} data-testid="new-quote-cameras" /></div>
            <div>
              <Label>Setup</Label>
              <Select value={form.installation_type} onValueChange={(v) => set("installation_type", v)}>
                <SelectTrigger data-testid="new-quote-install-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["wired", "wireless", "hybrid"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything from the call worth remembering" data-testid="new-quote-notes" /></div>

          <div className="flex items-center justify-between p-3 rounded-lg border border-border">
            <div>
              <div className="text-sm font-medium">Price it now</div>
              <div className="text-xs text-muted-foreground">Skip the reply step — set a price and email it straight away.</div>
            </div>
            <Switch checked={priceNow} onCheckedChange={setPriceNow} data-testid="new-quote-price-now-toggle" />
          </div>
          {priceNow && (
            <div className="space-y-3 p-3 rounded-lg bg-secondary/50">
              <div><Label>Price (AUD)</Label><Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="new-quote-price" /></div>
              <div><Label>Breakdown / notes for the customer</Label><Textarea rows={3} value={form.breakdown} onChange={(e) => set("breakdown", e.target.value)} data-testid="new-quote-breakdown" /></div>
              {!form.contact_email && <div className="text-xs text-amber-600">No email on file — the quote will be saved as priced, but nothing will be sent until you add one.</div>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="new-quote-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {priceNow && form.contact_email ? "Create & Email Quote" : "Create Quote"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
